import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, AlertCircle, CheckCircle, CalendarIcon, Clock, User, Truck, Boxes, Building2, Info } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function DriverView({ user, onLogout }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all'); // 'all' shows all pending deliveries by default
  const [selectedDate, setSelectedDate] = useState(null);
  const [view, setView] = useState('pending'); // pending, accepted, all

  useEffect(() => {
    fetchDeliveries();
  }, [period, selectedDate, view]);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('view', view);
      
      if (selectedDate) {
        params.append('specific_date', format(selectedDate, 'yyyy-MM-dd'));
      } else {
        params.append('period', period);
      }
      
      const response = await api.get(`/driver/deliveries?${params.toString()}`);
      setDeliveries(response.data);
    } catch (error) {
      console.error('Failed to load deliveries:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
    setSelectedDate(null); // Clear specific date when period changes
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setPeriod(''); // Clear period when specific date is selected
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setPeriod('today');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-slate-600">Loading deliveries...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="driver-view-title">
              {view === 'pending' ? 'Pending Deliveries' : view === 'accepted' ? 'My Accepted Deliveries' : 'All Deliveries'}
            </h1>
            <p className="text-slate-600 mt-1">
              {view === 'pending' 
                ? 'Deliveries awaiting driver acceptance' 
                : view === 'accepted' 
                  ? 'Deliveries you have accepted'
                  : 'All relevant deliveries'}
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* View Toggle */}
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[160px]" data-testid="view-select">
                <SelectValue placeholder="Select view" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">My Accepted</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>

            {/* Period Filter */}
            <Select value={period} onValueChange={handlePeriodChange} disabled={!!selectedDate}>
              <SelectTrigger className="w-[150px]" data-testid="period-select">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pending</SelectItem>
                <SelectItem value="today">Today Only</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>

            {/* Calendar Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant={selectedDate ? "default" : "outline"} 
                  className="gap-2"
                  data-testid="date-picker-btn"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'dd/MM/yyyy') : 'Pick Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Clear Filter Button */}
            {selectedDate && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearDateFilter}
                data-testid="clear-date-btn"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Deliveries Count */}
        <div className="text-sm text-slate-500">
          Showing {deliveries.length} {deliveries.length === 1 ? 'delivery' : 'deliveries'}
          {selectedDate && ` for ${format(selectedDate, 'dd/MM/yyyy')}`}
        </div>

        {/* Deliveries Grid */}
        {deliveries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">
                {view === 'pending' 
                  ? 'No pending deliveries awaiting acceptance'
                  : view === 'accepted'
                    ? 'You have not accepted any deliveries yet'
                    : 'No deliveries found'}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {period === 'today' && !selectedDate 
                  ? 'Try selecting a different date or time period'
                  : 'All deliveries have been processed'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {deliveries.map((delivery) => (
              <Card 
                key={delivery.id} 
                className={`border-l-4 ${
                  delivery.payload_status === 'ACCEPTABLE' ? 'border-l-green-500' : 'border-l-red-500'
                } hover:shadow-lg transition-shadow`}
                data-testid={`delivery-card-${delivery.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-bold">
                        {delivery.invoice_number || 'No Invoice'}
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        {formatDate(delivery.delivery_date)}
                        {delivery.supervisor_verified_at && (
                          <span className="ml-2 text-xs text-slate-400">
                            @ {formatTime(delivery.supervisor_verified_at)}
                          </span>
                        )}
                      </p>
                    </div>
                    {delivery.payload_status === 'ACCEPTABLE' ? (
                      <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Business Name */}
                  <div className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg">
                    <Building2 className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500">Business</p>
                      <p className="font-medium text-slate-900 truncate" title={delivery.customer_name}>
                        {delivery.customer_name || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Key Info Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {/* Status */}
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        delivery.status === 'Completed' ? 'bg-green-500' :
                        delivery.status === 'Waiting for Driver' ? 'bg-amber-500' :
                        delivery.status === 'Overweight' ? 'bg-red-500' :
                        'bg-slate-400'
                      }`} />
                      <span className="text-slate-600">{delivery.status}</span>
                    </div>

                    {/* Payload Status */}
                    <div className={`font-medium ${
                      delivery.payload_status === 'ACCEPTABLE' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {delivery.payload_status}
                    </div>

                    {/* Weight */}
                    <div className="col-span-2 flex items-center gap-2 text-slate-600">
                      <Truck className="h-4 w-4 text-slate-400" />
                      <span>
                        {delivery.total_pallet_weight_kg?.toFixed(0) || 0} / {delivery.vehicle_payload_kg?.toFixed(0) || 0} kg
                      </span>
                    </div>

                    {/* Pallets */}
                    <div className="flex items-center gap-2 text-slate-600">
                      <Boxes className="h-4 w-4 text-slate-400" />
                      <span>{delivery.pallet_count || 0} pallets</span>
                    </div>

                    {/* Supervisor */}
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="h-4 w-4 text-slate-400" />
                      <span className="truncate" title={delivery.supervisor_name}>
                        {delivery.supervisor_name || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Acceptance Status */}
                  <div className="flex items-center justify-between py-2 border-t border-slate-100">
                    <span className="text-sm text-slate-600">Accepted:</span>
                    <span className={`text-sm font-medium ${
                      delivery.driver_accepted ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      {delivery.driver_accepted ? (
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" /> Yes
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" /> Pending
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Reassignment Note */}
                  {delivery.reassignment_note && (
                    <div className="p-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-start gap-2">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <p>{delivery.reassignment_note}</p>
                    </div>
                  )}

                  {/* Overweight Warning */}
                  {delivery.payload_status === 'OVERWEIGHT' && (
                    <div className="p-2 bg-red-50 rounded-lg text-xs text-red-700">
                      <p className="font-medium flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        Overweight Delivery
                      </p>
                      <p className="mt-1">Cannot proceed - over by {delivery.over_by_kg?.toFixed(2) || 0} kg</p>
                    </div>
                  )}

                  {/* Action Button */}
                  <Link to={`/deliveries/${delivery.id}`} className="block">
                    <Button 
                      className="w-full" 
                      variant={delivery.driver_accepted ? 'outline' : 'default'}
                      data-testid={`view-delivery-${delivery.id}`}
                    >
                      {delivery.driver_accepted ? 'View Details' : 'Review & Accept'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
